<template>
  <div class="wt-asset-editor-action-picker">
    <ul class="list-inline" @click="clickInside" v-click-outside="clickOutside">
      <template v-for="(group, index) in groups">
        <li v-if="shouldShowGroup(group.id)" :key="index">
          <button
            class="btn -asset-editor-menu-button"
            ref="buttons"
            :class="[
              group.class,
              activeGroupId === group.id ? 'wt-button-active' : '',
            ]"
            @single-click="onSingleClickGroup(group, index, groups, $event)"
            @double-click="onDoubleClickGroup(group)"
            v-single-double-click="{ waitForDbl: !!group.doubleClick }"
          >
            <i class="wticon" :class="group.iconClass">{{ group.iconText }}</i>
            <span>{{ group.title }}</span>
          </button>
        </li>
      </template>

      <transition name="slide-fade">
        <li
          v-if="showDropdown"
          class="wt-asset-editor-action-picker-sub-menu"
          :class="{ visible: dropdownReady }"
          :style="dropdownStyleObj"
        >
          <ul
            ref="list"
            :class="{ 'has-scroll': hasDropdownScroll }"
            :style="menuStyle"
          >
            <template v-for="page in dropdownList">
              <li
                v-if="isPageActive(page)"
                :class="{ disable: shouldDisable(page) }"
                :title="shouldDisable(page) || page.title || page.displayText"
                :key="page.id"
                @click="choosePageFromDropdown(page)"
              >
                <img v-if="page.icon" :src="mixinSrc(page.icon)" />
                <div v-else-if="page.iconClass" class="icon-wrapper">
                  <i class="wticon" :class="page.iconClass"></i>
                </div>
                <span
                  :title="page.title || page.displayText"
                  v-text="page.title || page.displayText"
                />
              </li>
            </template>
          </ul>
        </li>
      </transition>
    </ul>
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's Menu Bar Vue (top bar)
 */
import app from 'app';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { mapGetters, mapState, mapActions } from 'vuex';

export default {
  data() {
    return {
      showDropdown: false,
      dropdownList: [],
      dropdownStyleObj: {
        transform: 'translateX(0px)',
        width: '0px',
      },
      menuHeight: 20,
      dropdownReady: false,
      hasDropdownScroll: false,
      activeGroupId: null,
      missingIconStrIndex: '',
    };
  },

  computed: {
    ...mapState('assetEditor', [
      'pages',
      'groups',
      'assetModel',
      'groupsWithExclusiveCustomOverlays',
    ]),
    ...mapGetters('assetEditor', [
      'presets',
      'blockedThemeEffects',
      'isBlackListed',
      'compareLayers',
      'AGD',
      'sceneLayoutCustomOverlay',
    ]),
    ...mapGetters('user', ['isImpededTemplatizer']),
    ...mapGetters('user', {
      userType: 'type',
    }),

    isImage() {
      return app.assetType.isIMAGE(this.assetModel);
    },

    isMap() {
      return app.assetType.isMAP(this.assetModel);
    },

    isSocialImage() {
      return app.assetType.isSOCIALIMAGE(this.assetModel);
    },

    isGif() {
      return app.assetType.isGIF(this.assetModel);
    },

    isInfographics() {
      return app.assetType.isINFOGRX(this.assetModel);
    },

    isWebpage() {
      return app.assetType.isWEBPAGE(this.assetModel);
    },

    isFragment() {
      return app.assetType.isFRAGMENT(this.assetModel);
    },

    isSocialFragment() {
      return app.assetType.isSOCIALFRAGMENT(this.assetModel);
    },

    menuStyle() {
      return {
        'max-height': this.menuHeight + 'px',
      };
    },
  },

  mounted() {
    bus.$on('layout-resize', this.onLayoutResize);
  },

  methods: {
    ...mapActions('assetEditor', ['changeCurrentPage']),

    onLayoutResize({ height }) {
      this.menuHeight = Math.max(height - 20, 0);
    },

    getPagesByGroup(groupId) {
      return this.pages.filter(
        (p) =>
          p.groupBy[0] && p.groupBy[0] === groupId && !(p.canAddNew === false)
      );
    },

    onSingleClickGroup(group, index, groups, e) {
      const groupPages = this.getPagesByGroup(group.id);

      if (groupPages.length) {
        if (group.changePageOnGroupClick) {
          // if (!this.blockedThemeEffects.includes(groupPages[0].id)) {
          this.changeCurrentPage(groupPages[0].id);
          // }
        } else {
          this.toggleDropdown(e, groupPages, group);
        }
      }
    },

    onDoubleClickGroup(group) {
      this.changeCurrentPage(group.doubleClick);
    },

    toggleDropdown(event, groupPages, group) {
      if (
        !this.showDropdown ||
        (this.showDropdown && this.activeGroupId === group.id)
      ) {
        // toggle dropdown
        this.showDropdown = !this.showDropdown;
      }

      this.dropdownReady = false;
      this.hasDropdownScroll = false;
      this.dropdownList = this.showDropdown ? groupPages : [];

      if (this.showDropdown) {
        // wait for render items to take effect, so we can calculate dropdown and arrow positions
        app.Vue.nextTick(() => {
          const buttonEl = this.getButtonNodeFromEvent(event.target);
          const list = this.$refs.list;
          this.dropdownStyleObj = this.getDropDownPosition(buttonEl);
          this.dropdownReady = true;

          // wait for dropdown to be visible, so we can check if has scroll to dropdown
          app.Vue.nextTick(() => {
            this.hasDropdownScroll = list.scrollHeight > list.offsetHeight;
          });
        });

        this.activeGroupId = group.id;
      } else {
        this.activeGroupId = null;
      }
    },

    isOnRightSide(buttonEl, modalEl) {
      const buttonMiddle = buttonEl.offsetLeft + buttonEl.offsetWidth / 2;
      const modalWidth = modalEl.offsetWidth;
      return buttonMiddle > modalWidth / 2;
    },

    getButtonNodeFromEvent(el) {
      return el.nodeName === 'BUTTON' ? el : el.parentElement;
    },

    getDropDownPosition(buttonEl) {
      const modalEl = this.$el.closest('.js-modal-content');
      const list = this.$refs.list;
      const buttonMiddle = buttonEl.offsetLeft + buttonEl.offsetWidth / 2;
      const isOnRight = this.isOnRightSide(buttonEl, modalEl);
      let menuLeft;
      if (isOnRight) {
        menuLeft = buttonMiddle - (list.offsetWidth * 2) / 3;
      } else {
        menuLeft = buttonMiddle - list.offsetWidth / 3;
      }

      const maxLeft = modalEl.offsetWidth - list.offsetWidth - 20;
      menuLeft = Math.min(menuLeft, maxLeft);
      menuLeft = Math.max(menuLeft, 10);

      return {
        transform: `translateX(${menuLeft}px)`,
        width: `${list.offsetWidth}px`,
      };
    },

    choosePageFromDropdown(page) {
      // if page should be disabled, return.
      if (this.shouldDisable(page)) {
        return;
      }

      if (this.isImpededTemplatizer && this.alreadyHasSceneLayout(page)) {
        return;
      }

      this.changeCurrentPage(page.id);
    },

    /**
     * Don't allow multiple overlays with isSceneLayout per asset
     * @param page
     * @returns {boolean}
     */
    alreadyHasSceneLayout(page) {
      if (page.isSceneLayout === true) {
        // check it's unique
        const sceneLayouts = _.filter(this.AGD.customOverlays, (overlay) => {
          const preset = this.presets.find(
            (preset) => preset.id === overlay.id
          );
          return preset.id !== page.id && preset.isSceneLayout;
        });

        // if already exists show warning notification
        if (sceneLayouts.length > 0) {
          app.notificationController.push({
            text: 'You already selected an overlay for this scene, please delete prior to adding a new one',
            type: 'WARN',
            timeout: app.TIME.second(5),
            uniqueGroupName: true,
          });
          return true;
        }
      }

      return false;
    },

    isZoomActive() {
      return (this.isImage && !this.isInfographics) || this.isWebpage;
    },

    isSubtitlesActive() {
      return this.isFragment || this.isSocialFragment;
    },

    isCropActive() {
      return (
        (this.isImage ||
          this.isGif ||
          this.isFragment ||
          this.isSocialFragment ||
          this.isSocialImage) &&
        !this.isMap
      );
    },

    genericIsGroupActive(groupId) {
      const pages = this.getPagesByGroup(groupId);
      return (
        !_.isEmpty(pages) &&
        pages.some((p) => !this.isBlackListed(p.id) && !p.disabled)
      );
    },

    isPageActive(page) {
      if (page.id === 'subtitles') {
        return this.isSubtitlesActive();
      }
      return (
        !this.isBlackListed(page.id) &&
        !page.disabled &&
        !this.blockedThemeEffects.includes(page.id)
      );
    },

    shouldShowGroup(groupId) {
      const activeGroups = {
        ZOOM: this.isZoomActive,
        CROP: this.isCropActive,
        OVERLAY: this.genericIsGroupActive,
        EFFECT: this.genericIsGroupActive,
        OVERLAY_ASSET: () => false,
        AUDIO: () => false,
        TEXT: this.genericIsGroupActive,
      };
      return activeGroups[groupId.toUpperCase()](groupId);
    },

    shouldDisable: function (page) {
      if (
        page.isSceneLayout &&
        page.cropRectangle &&
        this.sceneLayoutCustomOverlay &&
        this.sceneLayoutCustomOverlay.id !== page.id
      ) {
        return `Only one layout can be selected. Remove "${
          this.sceneLayoutCustomOverlay.title ||
          this.sceneLayoutCustomOverlay.displayText
        }" first.`;
      }
      const layer = page.layers && page.layers[0];
      if (!layer || !this.groupsWithExclusiveCustomOverlays.includes(layer)) {
        return false;
      }

      const firstAgdAttr = page.agdAttributes && page.agdAttributes[0];
      const parentId = firstAgdAttr && firstAgdAttr.split('.')[0];

      const compareLayers = this.compareLayers({
        parentId: parentId,
        layer: layer,
        index: 0,
      });

      return (
        !_.isEmpty(compareLayers) &&
        !_.find(compareLayers, (val) => val.id === page.id)
      );
    },

    clickOutside() {
      this.resetDropDown();
    },

    clickInside(e) {
      if (
        this.$refs.buttons.some((b) => b.contains(e.target)) ||
        (this.$refs.list && this.$refs.list.contains(e.target))
      ) {
        return;
      }

      this.resetDropDown();
    },

    resetDropDown() {
      this.dropdownList = [];
      this.showDropdown = false;
      this.activeGroupId = null;
      this.dropdownReady = false;
      this.hasDropdownScroll = false;
    },
  },
};
</script>
